import React, { useCallback, useMemo, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { FormikCheckbox, FormikFormControl, FormikInput } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import {
  AuthenticationMethod,
  AuthenticationPhases,
  CustomEventNames,
} from 'features/analytics/constants';
import { useSignupCompletion } from 'features/auth/hooks/use-signup-completion';
import { logOTPSignUpError, logSignUpEvent } from 'features/auth/utils/analytics';
import { useFeatureAccountForm } from 'features/content/hooks/use-feature-account-form';
import { useFeatureDisclaimers } from 'features/content/hooks/use-feature-disclaimers';
import { Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { StyleSheet } from 'react-native';
import { Divider } from 'ucl/components';

import { FormikBirthdayInput } from 'components/formik/birthday-input';
import { FormikCountrySelect } from 'components/formik/country-select';
import { Box, Typography } from 'components/ui';
import { VisuallyHidden } from 'components/ui/visually-hidden';
import { PromotionalEmailsLabel } from 'components/user-info-form/checkbox-labels';
import { OptInLabel, TermsLink } from 'components/user-info-form/styled';
import { logPromotionalEmailPreferenceChanged } from 'experiments/signup-flow-redesign/analytics';
import { getLocaleStringText } from 'experiments/signup-flow-redesign/get-locale-string-text';
import { getSignupPromotionalEmailPreference } from 'experiments/signup-flow-redesign/get-signup-promotional-email-preference';
import { ProviderType } from 'generated/graphql-gateway';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useLoyaltyContent } from 'hooks/loyalty/use-loyalty-content';
import { useRoute } from 'hooks/navigation/use-route';
import useErrorModal from 'hooks/use-error-modal';
import { useFieldIsRequired } from 'hooks/use-field-is-required';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import AuthActions from 'pages/authentication/components/auth-actions';
import { DialogStaticPage } from 'pages/authentication/components/dialog-static-page';
import SignUpHeadline from 'pages/authentication/components/sign-up-headline/sign-up-headline';
import { SignupAccordion } from 'pages/authentication/components/signup-accordion/signup-accordion';
import UpdateSessionStorage, {
  staticUserForm,
} from 'pages/authentication/sign-up/update-session-storage';
import { CampaignRouteParams } from 'pages/campaigns/types';
import { useGetExternalCampaignBySlug } from 'pages/campaigns/use-get-external-campaign-by-slug';
import { useAuthContext } from 'state/auth';
import { SIGN_IN_FAIL } from 'state/auth/constants';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_REGION as DEFAULT_REGION } from 'state/intl/constants';
import { t, tRich } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  SignUpFieldsVariations,
  defaultSignUpFieldsVariation,
} from 'state/launchdarkly/variations';
import { useLocationContext } from 'state/location';
import { useUIContext } from 'state/ui';
import { getDesignToken, spacing } from 'styles/constants';
import { isWeb } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { Region } from 'utils/environment/types';
import { GraphQLErrorCodes, graphQLErrorsIncludeExtensionCode } from 'utils/errors';
import { dobStringToObj, formatDateObject, sanitizePhoneNumber } from 'utils/form';
import { ISOs } from 'utils/form/constants';
import { ISignupFormData } from 'utils/form/types';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import ExistingEmailDialog from './existing-email-dialog';
import initialFormState from './initial-form-state';
import PhoneNumberField from './phone-number-field';
import { IOverrides, ISignup, OverridableFields } from './types';
import { generateDisplayedErrors, validateSignupFormError } from './utils';

const LEGACY_USER_CREATE_CONFLICT = 'LEGACY_USER_CREATE_CONFLICT';
const USERNAME_EXISTS_EXCEPTION = 'UsernameExistsException';
const ZERO_BOUNCE = 'zerobounce';

/**
 * LaunchDarkly has a flag that decides if we return an error
 * via GraphQL or as a network error.
 *
 * @param error The error returned from graphql
 *
 * @returns {boolean}
 */
const hasZeroBounceError = (error: ApolloError | Error): boolean => {
  const zeroBounceGraphqlValidationFailed = graphQLErrorsIncludeExtensionCode(
    error instanceof ApolloError ? error.graphQLErrors : [],
    GraphQLErrorCodes.ZERO_BOUNCE_VALIDATION_FAILED
  );

  const zeroBounceNetworkError = error.message.toLowerCase().includes(ZERO_BOUNCE);

  return zeroBounceGraphqlValidationFailed || zeroBounceNetworkError;
};

const SignUp = ({
  skipRedirect,
  authCampaignExtraFields,
  initialEmail = '',
  shouldDisableHeadlineLinks = false,
}: ISignup) => {
  useAuthRedirects({ skip: skipRedirect! });

  const {
    signUp,
    openSignInModal,
    originLocation,
    modalAuthState: {
      user: modalAuthStateUser,
      loginForVendorIsLoading,
      providerType,
      shouldWaitLoginForVendor,
    },
    loading,
  } = useAuthContext();

  const { setSignUpEmail } = useLocationContext();

  const { params } = useRoute<
    CampaignRouteParams & {
      email?: string;
      name?: string;
      authenticationMethod?: string;
      socialAuthenticationType?: ProviderType;
    }
  >();

  const { campaign } = useGetExternalCampaignBySlug({
    slug: params?.campaignSlug ?? '',
  });

  const { navigate } = useRouter();

  const { pathname } = useRoute();

  const [errorMessageToAnnounce, setErrorMessageToAnnounce] = useState('');
  const [formSuccessfullySubmitted, setFormSuccessfullySubmitted] = useState(false);
  const [isSignUpLoading, setSignUpIsLoading] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const { region } = useLocale();
  const enableCustomBirthdayInput = useFlag(LaunchDarklyFlag.ENABLE_CUSTOM_BIRTHDAY_INPUT);
  const enableZeroBounceErrorModal = useFlag(LaunchDarklyFlag.ENABLE_ZERO_BOUNCE_ERROR_MODAL);
  const enableSignupEmailMarketingDefaultOptIn = useFlag(
    LaunchDarklyFlag.ENABLE_SIGNUP_EMAIL_MARKETING_DEFAULT_OPT_IN
  );
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();

  const signUpFieldsVariations =
    useFlag<SignUpFieldsVariations>(LaunchDarklyFlag.SIGN_UP_FIELDS_VARIATIONS) ||
    defaultSignUpFieldsVariation;

  const requiredOverrides = signUpFieldsVariations.phone
    ? undefined
    : { [OverridableFields.PHONE]: { required: false } };

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Sign In Failure',
  });

  const zipCodeIsRequired = useFieldIsRequired(region, validationConfig, 'zipcode');
  const birthdayIsRequired = useFieldIsRequired(region, validationConfig, 'dob');
  const phoneNumberIsRequired = useFieldIsRequired(region, validationConfig, 'phoneNumber');

  const [showSignInWithExistingEmailDialog, setShowSignInWithExistingEmailDialog] = useState(false);
  const [ZeroBounceErrorDialog, openZeroBounceErrorDialog] = useErrorModal({
    onConfirm: () => navigate(routes.support),
    modalAppearanceEventMessage: 'Error: Sign In Failure [Zero Bounce]',
  });

  const [ForterSignUpErrorDialog, openForterSignUpErrorDialog] = useErrorModal({
    onConfirm: () => navigate(routes.support),
    modalAppearanceEventMessage: 'Error: Sign Up Failure [Forter]',
  });

  const { featureDisclaimers } = useFeatureDisclaimers();
  const { birthdayLegalText } = useLoyaltyContent();

  const recoverSignIn = useCallback(
    (submittedEmail: string) => {
      setSignUpIsLoading(false);
      setSignUpEmail(submittedEmail);
      openSignInModal({ errorMessage: t('signUpSuccessSignInFail') });
    },
    [openSignInModal, setSignUpEmail]
  );
  const {
    startCompletion,
    isLoading: isSignUpCompleteLoading,
    hasFailed: hasCompletionFailed,
  } = useSignupCompletion({ onError: recoverSignIn });

  const { featureAccountForm } = useFeatureAccountForm();

  const { country, signUpDisclaimers, signUpMarketDisclaimers, phoneNumber } =
    featureDisclaimers || {};
  const birthdayDisclaimer = getLocaleStringText(birthdayLegalText);
  const countryDisclaimer = getLocaleStringText(country?.locale);
  const phoneNumberDisclaimer = getLocaleStringText(phoneNumber?.locale);
  const postalZipCodeDisclaimer = getLocaleStringText(
    featureAccountForm?.postalOrZipDisclaimer?.locale
  );
  const signUpDisclaimer = getLocaleStringText(signUpDisclaimers?.locale);
  const signUpMarketDisclaimer = getLocaleStringText(signUpMarketDisclaimers?.locale);
  const signInLink = useRef<import('react-native').Text>(null);

  const defaultSignupValues =
    staticUserForm.current ||
    initialFormState({
      enableCustomBirthdayInput,
      regionIso: ISOs[region as keyof typeof ISOs],
      defaultWantsPromotionalEmails:
        signUpFieldsVariations.wantsPromotionalEmails && enableSignupEmailMarketingDefaultOptIn,
    });

  const initialUserFormState: ISignupFormData = {
    ...defaultSignupValues,
    name: modalAuthStateUser?.name || params.name || defaultSignupValues.name,
    email:
      initialEmail ||
      modalAuthStateUser?.email ||
      params.email ||
      syncStorage.getString(SyncStorageKey.LOGIN_EMAIL) ||
      defaultSignupValues.email,
    dob: (typeof defaultSignupValues.dob === 'string'
      ? dobStringToObj(defaultSignupValues.dob)
      : defaultSignupValues.dob) || { month: '', day: '', year: '' },
  };

  const handleAccordionInteraction = (isActive: boolean) => {
    setIsAccordionOpen(isActive);
  };

  const handleSubmitError = useCallback(
    (error: any, values: ISignupFormData, actions: FormikHelpers<ISignupFormData>) => {
      logger.warn(error);
      setSignUpIsLoading(false);

      // Determine error type
      const forterSignUpDecline = graphQLErrorsIncludeExtensionCode(
        error.graphQLErrors,
        GraphQLErrorCodes.SIGNUP_VALIDATION_ERROR
      );
      const emailAlreadyExists = graphQLErrorsIncludeExtensionCode(
        error.graphQLErrors,
        GraphQLErrorCodes.AUTH_EMAIL_ALREADY_EXIST
      );
      const legacyErrorRegexp = new RegExp(LEGACY_USER_CREATE_CONFLICT, 'g');
      const userAlreadyExists =
        error.code === USERNAME_EXISTS_EXCEPTION ||
        error.message.match(legacyErrorRegexp) ||
        emailAlreadyExists;
      const signInFailure = error.code === SIGN_IN_FAIL;
      const zeroBounceFailure = enableZeroBounceErrorModal && hasZeroBounceError(error);

      // Perform next actions based on which error has been received
      if (forterSignUpDecline) {
        openForterSignUpErrorDialog({
          error,
          message: t('forterSignUpError'),
          title: t('duplicateAccounts'),
        });
        setSignUpEmail(values.email);
        actions.setFieldError('email', t('accountWithEmailAlreadyExists'));
        logOTPSignUpError(t('forterSignUpError'));
        return;
      }
      if (userAlreadyExists) {
        const errorMessage = t('accountWithEmailAlreadyExists');
        setShowSignInWithExistingEmailDialog(true);
        setSignUpEmail(values.email);
        actions.setFieldError('email', errorMessage);
        logOTPSignUpError(errorMessage);
        return;
      }
      if (signInFailure) {
        const errorMessage = t('signUpSuccessSignInFail');

        setSignUpEmail(values.email);
        openSignInModal({ errorMessage });
        logOTPSignUpError(errorMessage);

        return;
      }
      if (zeroBounceFailure) {
        openZeroBounceErrorDialog({
          error,
          message: t('theEmailAddressIsInvalid'),
          title: t('invalidEmail'),
        });
        return;
      }
      openErrorDialog({
        error,
        message: t('signUpError'),
      });
    },
    [
      enableZeroBounceErrorModal,
      openErrorDialog,
      openForterSignUpErrorDialog,
      openZeroBounceErrorDialog,
      setSignUpEmail,
      openSignInModal,
    ]
  );

  const onSubmit = useCallback(
    async (values: ISignupFormData, actions: FormikHelpers<ISignupFormData>) => {
      if (hasCompletionFailed) {
        recoverSignIn(email);
        return;
      }
      setSignUpIsLoading(true);

      syncStorage.setString(SyncStorageKey.LOGIN_EMAIL, values.email);
      // need to do this so that TH uses the correct country in a
      // case where user switches locales in the middle of signing up
      let signupCountry = values.country;
      // only if we aren't giving the user the specific country option
      if (!signUpFieldsVariations.country) {
        signupCountry = ISOs[region] || ISOs[DEFAULT_REGION as keyof typeof ISOs];
      }

      const isWalmartLandingPage = pathname === routes.walmart;
      const source = campaign?.slug ?? (isWalmartLandingPage ? 'walmart' : undefined);

      // We can only send a string for DOB
      const dob = formatDateObject(values.dob);
      const signupValues = {
        ...values,
        phoneNumber: await sanitizePhoneNumber(values.phoneNumber, signupCountry),
        dob,
        country: signupCountry,
        email: values.email.trim(),
        wantsPromotionalEmails: getSignupPromotionalEmailPreference({
          isFieldVisible: signUpFieldsVariations.wantsPromotionalEmails,
          wantsPromotionalEmails: values.wantsPromotionalEmails,
        }),
        zipcode: values.zipcode?.toUpperCase(),
        ...(source && { crmAttributes: { source } }),
      };

      // Bypass the default internal sign in here.
      // We are going to sign in using the JWT we get from the sign up mutation.
      const bypassSignIn = () => Promise.resolve();

      try {
        const { jwt } = await signUp(
          signupValues,
          bypassSignIn,
          params.authenticationMethod,
          params.socialAuthenticationType || providerType
        );

        const socialAuthenticationType = params.socialAuthenticationType;
        const authenticationMethod = socialAuthenticationType
          ? AuthenticationMethod.SOCIAL
          : AuthenticationMethod.OTP;

        logSignUpEvent({
          phase: AuthenticationPhases.COMPLETE,
          authenticationMethod,
          socialAuthenticationType,
          emailMarketingOptIn: signupValues.wantsPromotionalEmails,
        });

        // The state shouldn't be changed to false if a login for a vendor is expected.
        // This is to avoid a race condition among the functions completing the signup flow in a campaign.
        // The sequence involves user registration, auto-login, JWT validation, and finally,
        // the loginForVendor mutation within the campaign framework
        // If the state is prematurely changed to false, it can intermittent re-enable the submit button,
        // Campagin Framework: 'pages/campaigns/campaigns.tsx'
        if (!shouldWaitLoginForVendor) {
          setSignUpIsLoading(false);
        }

        setEmail(signupValues.email);
        setFormSuccessfullySubmitted(true);

        if (jwt) {
          startCompletion({ jwt, email: signupValues.email });
        }
      } catch (error) {
        handleSubmitError(error, values, actions);
      }
    },
    [
      campaign,
      handleSubmitError,
      params,
      region,
      signUp,
      signUpFieldsVariations.country,
      signUpFieldsVariations.wantsPromotionalEmails,
      shouldWaitLoginForVendor,
      pathname,
      providerType,
      startCompletion,
      hasCompletionFailed,
      recoverSignIn,
      email,
    ]
  );

  const TermsOfServiceLabel = () => {
    const { setDialogStaticPageState } = useUIContext();

    const openDialogStaticPage = (page: string) => setDialogStaticPageState({ isOpen: true, page });

    const privacyPolicyLink = (
      <TermsLink onPress={() => openDialogStaticPage(routes.privacyPolicy)}>
        {t('privacyPolicy')}
      </TermsLink>
    );
    const rewardsPolicyRoute =
      region === Region.US ? routes.royalPerksTerms : routes.termsConditionsRewards;

    const rewardsPolicyLink = (
      <TermsLink onPress={() => openDialogStaticPage(rewardsPolicyRoute)}>
        {t('rewardsPolicy')}
      </TermsLink>
    );

    const termsOfServiceLink = (
      <TermsLink onPress={() => openDialogStaticPage(routes.termsOfService)}>
        {t('termsOfService')}
      </TermsLink>
    );

    return (
      <OptInLabel>
        {tRich('acceptTermsAndPrivacyIntro', {
          termsOfServiceLink,
          privacyPolicyLink,
          rewardsPolicyLink,
        })}
      </OptInLabel>
    );
  };

  const shouldDisplayMarketDisclaimer = useMemo(() => {
    return isSignUpAfterCartEnabled && !!signUpMarketDisclaimer && originLocation === routes.cart;
  }, [isSignUpAfterCartEnabled, originLocation, signUpMarketDisclaimer]);

  const regionValue = Region[region];

  const generateErrorMessageForAccessibility = (errors: FormikErrors<ISignupFormData>) => {
    const errorEntries = Object.values(errors);
    if (errorEntries.length > 0) {
      let errorsToAnnounce = t('createAnAccountErrors', { count: errorEntries.length });
      errorsToAnnounce = `${errorsToAnnounce} ${errorEntries
        .map((error, index) => {
          let errorMessage = '';
          if (errorEntries.length > 1) {
            errorMessage = `${t('error', 'Error')} ${index + 1}: `;
          }
          return `${errorMessage} ${error} `;
        })
        .join('')}`;

      setErrorMessageToAnnounce(errorsToAnnounce);
    } else {
      setErrorMessageToAnnounce('');
    }
  };

  return (
    <>
      {formSuccessfullySubmitted && (
        <VisuallyHidden accessibilityLabel={t('youHaveSuccessfullySubmittedTheForm')}>
          <Box accessibilityRole="alert">
            <Typography>{t('signupEmailSent', { email })}</Typography>
          </Box>
        </VisuallyHidden>
      )}
      <Formik
        initialValues={initialUserFormState}
        onSubmit={onSubmit}
        validate={validateSignupFormError({
          region: regionValue,
          overrides: requiredOverrides as IOverrides,
          signUpFieldsVariations,
          simpleValidation: false,
        })}
      >
        {({
          handleSubmit,
          errors,
          setFieldValue,
          touched,
          setTouched,
          validateForm,
          values,
          setFieldTouched,
        }: FormikProps<ISignupFormData>) => {
          const isDobInvalid = Boolean(touched.dob && errors.dob);

          const accessibilityLabel = t('thereIsAnErrorInThisForm');

          const isLoading =
            isSignUpLoading ||
            isSignUpCompleteLoading ||
            loading ||
            (shouldWaitLoginForVendor && loginForVendorIsLoading);

          const zipCodeKey = values.country === ISOs.USA ? 'zipCode' : 'postalCode';
          const zipCodeLabel = t(zipCodeKey);

          const handlePromotionalEmailsChange = (isSelected: boolean) => {
            setFieldValue('wantsPromotionalEmails', isSelected);
            logPromotionalEmailPreferenceChanged(isSelected);
          };

          const handleCreateAccountPress = async () => {
            if (isLoading) {
              return;
            }
            if (hasCompletionFailed) {
              recoverSignIn(email);
              return;
            }
            const error = await validateForm();
            generateErrorMessageForAccessibility(error);

            const touchedFields = Object.entries(error).reduce((accum, [key, value]) => {
              if (!value) {
                return { ...accum };
              }
              return {
                [key]: true,
                ...accum,
              };
            }, {});
            setTouched(touchedFields);

            if (Object.keys(error).length !== 0) {
              logOTPSignUpError(Object.values(error).join(' '));
              return;
            }
            handleSubmit();
          };

          return (
            <Box>
              <SignUpHeadline disableLink={shouldDisableHeadlineLinks} />
              <Divider background="blackOpacity.30" marginTop="$6" marginBottom="$6" />
              <Typography accessibilityLevel={2} variant="headerThree">
                {t('createAnAccount')}
              </Typography>
              <Typography ref={signInLink} style={styles.subheader}>
                {t('createAnAccountSubheader', { email: values.email })}
              </Typography>

              {/* @ts-expect-error TS(2322) FIXME: */}
              {generateDisplayedErrors(errors, touched).length ? (
                <VisuallyHidden role="alert" accessibilityLabel={accessibilityLabel}>
                  <Typography variant="copyOne" style={styles.accessibilityError}>
                    {accessibilityLabel}
                  </Typography>
                </VisuallyHidden>
              ) : null}

              {shouldDisplayMarketDisclaimer && (
                <Typography
                  testID="signup-market-disclaimer"
                  variant="copyTwo"
                  style={styles.marketDisclaimer}
                >
                  {signUpMarketDisclaimer}
                </Typography>
              )}
              {signUpFieldsVariations.country && (
                <Box style={styles.fieldContainer}>
                  <FormikCountrySelect
                    name="country"
                    testID="signup-country-input"
                    label={t('country')}
                    disclaimer={countryDisclaimer}
                    onChange={value => {
                      setFieldValue('country', value.toUpperCase());
                    }}
                    value={values.country}
                  />
                  <Typography variant="copyOne" color={getDesignToken('color-error')} fontSize="xs">
                    {errors.country}
                  </Typography>
                </Box>
              )}

              <Box style={styles.fieldContainer}>
                <FormikFormControl
                  label={t('firstName')}
                  name="name"
                  isRequired
                  isDisabled={isLoading}
                >
                  <FormikInput
                    inputProps={{
                      accessibilityLabel: t('name'),
                      placeholder: t('firstName'),
                      testID: 'signup-name-input',
                      placeholderTextColor: '#979797',
                      autoComplete: 'name-given',
                      maxLength: 100,
                      returnKeyType: 'done',
                      onSubmitEditing: handleCreateAccountPress,
                    }}
                  />
                </FormikFormControl>
              </Box>

              {signUpFieldsVariations.phone && (
                <>
                  <PhoneNumberField
                    isRequired={phoneNumberIsRequired}
                    hintMessage={phoneNumberDisclaimer}
                    onSubmitEditing={handleCreateAccountPress}
                  />
                  {!!phoneNumberDisclaimer && (
                    <Typography variant="copyTwo" style={styles.disclaimer}>
                      {phoneNumberDisclaimer}
                    </Typography>
                  )}
                </>
              )}
              <SignupAccordion
                title={t('optionalInformation')}
                onInteract={handleAccordionInteraction}
                isOpen={isDobInvalid || isAccordionOpen}
                disabled={isDobInvalid}
                id={'optional-information'}
              >
                {signUpFieldsVariations.zip && (
                  <Box style={styles.fieldContainer}>
                    <FormikFormControl
                      label={zipCodeLabel}
                      name="zipcode"
                      isRequired={zipCodeIsRequired}
                      isDisabled={isLoading}
                    >
                      <FormikInput
                        inputProps={{
                          placeholder: zipCodeLabel,
                          testID: 'signup-zipcode-input',
                          placeholderTextColor: '#979797',
                          maxLength: 10,
                          autoComplete: 'postal-code',
                          returnKeyType: 'done',
                          onSubmitEditing: handleCreateAccountPress,
                        }}
                      />
                    </FormikFormControl>
                  </Box>
                )}
                {!!authCampaignExtraFields && authCampaignExtraFields}
                {!!postalZipCodeDisclaimer && (
                  <Typography variant="copyTwo" style={styles.disclaimer}>
                    {postalZipCodeDisclaimer}
                  </Typography>
                )}
                {signUpFieldsVariations.dob && (
                  <Box style={styles.fieldContainer}>
                    <FormikFormControl name="dob" label={t('dateOfBirth')}>
                      <FormikBirthdayInput
                        disabled={isLoading}
                        day={values.dob.day}
                        month={values.dob.month}
                        year={values.dob.year}
                        isRequired={birthdayIsRequired}
                        onBlur={inputName => setFieldTouched(`dob.${inputName}`, true)}
                        onChange={(inputName, inputText) =>
                          setFieldValue(`dob.${inputName}`, inputText)
                        }
                        testID="signup-dob"
                        dobErrors={errors.dob}
                        dobTouched={touched.dob}
                        hintMessage={birthdayDisclaimer}
                      />
                    </FormikFormControl>
                  </Box>
                )}
                {!!signUpDisclaimer && (
                  <Typography variant="copyTwo" style={styles.disclaimer}>
                    {signUpDisclaimer}
                  </Typography>
                )}
              </SignupAccordion>

              {signUpFieldsVariations.wantsPromotionalEmails && (
                <Box style={styles.fieldContainer}>
                  <FormikCheckbox
                    name="wantsPromotionalEmails"
                    checkboxProps={{
                      onChange: handlePromotionalEmailsChange,
                      alignItems: 'flex-start',
                      value: 'true',
                      testID: 'signup-promotional-emails',
                      accessibilityLabel: t('signUpForPromotionalEmails'),
                      isDisabled: isLoading,
                    }}
                  >
                    <PromotionalEmailsLabel />
                  </FormikCheckbox>
                </Box>
              )}

              {signUpFieldsVariations.agreesToTermsOfService && (
                <Box style={styles.fieldContainer}>
                  <FormikFormControl
                    name="agreesToTermsOfService"
                    isRequired
                    isDisabled={isLoading}
                  >
                    <FormikCheckbox
                      checkboxProps={{
                        onChange: isSelected => setFieldValue('agreesToTermsOfService', isSelected),
                        alignItems: 'flex-start',
                        value: 'false',
                        testID: 'signup-agreeToTermsOfService',
                        accessibilityLabel: t('agreeToTermsOfService'),
                      }}
                    >
                      <TermsOfServiceLabel />
                    </FormikCheckbox>
                  </FormikFormControl>
                </Box>
              )}

              <Box style={styles.buttonWrapper}>
                <AuthActions
                  onPress={handleCreateAccountPress}
                  eventName={hasCompletionFailed ? undefined : CustomEventNames.SIGN_UP_SUBMITTED}
                  primaryActionQaId="signup-button"
                  primaryActionLabel={hasCompletionFailed ? t('signIn') : t('createAnAccount')}
                  primaryActionIsLoading={isLoading}
                  primaryActionDisabled={(!hasCompletionFailed && isDobInvalid) || isLoading}
                />
              </Box>
              {/* TODO: RN - Migrate FocusOnerror */}
              {/* <FocusOnError
                  setHasJustSubmitted={setHasJustSubmitted}
                  hasJustSubmitted={hasJustSubmitted}
                /> */}
              <UpdateSessionStorage />

              <Box
                accessible={false}
                accessibilityLabel={errorMessageToAnnounce}
                accessibilityLiveRegion="polite"
                {...(isWeb && { 'aria-live': 'polite' })}
                style={styles.liveRegion}
              />
            </Box>
          );
        }}
      </Formik>
      <ErrorDialog />
      <ExistingEmailDialog
        showDialog={showSignInWithExistingEmailDialog}
        dismiss={() => setShowSignInWithExistingEmailDialog(false)}
      />
      <ForterSignUpErrorDialog
        testID="forter-signup-error-modal"
        buttonLabel={t('contactSupport')}
        showCloseButton
      />
      <ZeroBounceErrorDialog
        testID="zero-bounce-error-modal"
        buttonLabel={t('contactSupport')}
        showCloseButton
      />
      <DialogStaticPage />
    </>
  );
};

export default SignUp;

const styles = StyleSheet.create({
  accessibilityError: {
    marginBottom: spacing.$2,
  },
  buttonWrapper: {
    marginTop: spacing.$3,
  },
  disclaimer: {
    marginBottom: spacing.$3,
  },
  fieldContainer: {
    marginBottom: spacing.$1,
  },
  liveRegion: {
    height: 0,
    opacity: 0,
    width: 0,
  },
  marketDisclaimer: {
    marginBottom: spacing.$4,
    textAlign: 'center',
  },
  subheader: {
    marginBottom: spacing.$4,
  },
});
